/**
 * YouTube Music, as far as the public API allows.
 *
 * There is no public YouTube Music API. What the Data API gives us is the
 * same Google account's playlists, liked videos and search — which is what
 * YouTube Music is built on. Playback goes through the official IFrame player.
 */

import { OPTIONS } from '../config/defaults.ts';
import type { Playlist, Track } from '../types/media.ts';
import { PAGE, type Page, type Profile, YouTubeClient } from './youtube-client.ts';
import {
  type PlaylistItem,
  type PlaylistResource,
  playlistItemToTrack,
  playlistResourceToPlaylist,
  type SearchItem,
  searchItemToTrack,
  type VideoItem,
  videoToTrack,
} from './youtube-shape.ts';

const MAX_PLAYLISTS = 200;

interface ChannelItem {
  id?: string;
  snippet?: { title?: string; thumbnails?: { default?: { url: string } } };
}

export class YouTubeLibrary extends YouTubeClient {
  /** Playlists the account owns — YouTube Music playlists appear here. */
  async myPlaylists(): Promise<Playlist[]> {
    const items = await this.pages<PlaylistResource>(
      'playlists',
      { part: 'snippet,contentDetails', mine: 'true' },
      MAX_PLAYLISTS,
    );
    return items.map(playlistResourceToPlaylist);
  }

  /** Liked videos — the closest thing the API offers to "Your Likes". */
  async likedTracks(max = OPTIONS.maxResults): Promise<Track[]> {
    const r = await this.api<Page<VideoItem>>('videos', {
      part: 'snippet,contentDetails',
      myRating: 'like',
      maxResults: Math.min(max, PAGE),
    });
    return (r.items ?? []).map(videoToTrack);
  }

  async playlistTracks(playlistId: string, max = 100): Promise<Track[]> {
    const items = await this.pages<PlaylistItem>(
      'playlistItems',
      { part: 'snippet,contentDetails', playlistId },
      max,
    );
    const durations = await this.durations(ids(items, (i) => i.contentDetails?.videoId));
    return items
      .map((i) => playlistItemToTrack(i, durations))
      .filter((t): t is Track => t !== null);
  }

  async search(q: string, max = OPTIONS.maxResults): Promise<Track[]> {
    const r = await this.api<Page<SearchItem>>('search', {
      part: 'snippet',
      type: 'video',
      q,
      maxResults: Math.min(max, PAGE),
      /* Category 10 is Music; embeddable-only keeps unplayable hits out. */
      videoCategoryId: '10',
      videoEmbeddable: 'true',
    });
    const items = r.items ?? [];
    const durations = await this.durations(ids(items, (i) => i.id?.videoId));
    return items.map((i) => searchItemToTrack(i, durations)).filter((t): t is Track => t !== null);
  }

  /**
   * Durations come from a separate endpoint, batched fifty at a time. They are
   * cosmetic, so a failure here leaves the rows without a time rather than
   * failing the whole listing.
   */
  async durations(videoIds: readonly string[]): Promise<Map<string, number>> {
    const map = new Map<string, number>();
    for (let i = 0; i < videoIds.length; i += PAGE) {
      const chunk = videoIds.slice(i, i + PAGE);
      if (!chunk.length) break;
      try {
        const r = await this.api<Page<VideoItem>>('videos', {
          part: 'contentDetails',
          id: chunk.join(','),
        });
        for (const v of r.items ?? []) map.set(v.id, videoToTrack(v).duration);
      } catch {
        /* carry on without them */
      }
    }
    return map;
  }

  protected async fetchProfile(): Promise<Profile> {
    try {
      const ch = await this.api<Page<ChannelItem>>('channels', { part: 'snippet', mine: 'true' });
      const c = ch.items?.[0];
      return {
        name: c?.snippet?.title ?? 'YouTube account',
        avatar: c?.snippet?.thumbnails?.default?.url ?? null,
        channelId: c?.id ?? null,
      };
    } catch {
      /* The account is signed in even if its channel details are unreadable. */
      return { name: 'YouTube account', avatar: null, channelId: null };
    }
  }
}

const ids = <T>(items: readonly T[], pick: (item: T) => string | undefined): string[] =>
  items.map(pick).filter((v): v is string => !!v);
